// Codex client helpers. Reads through the anon Supabase client — codex_*
// tables are public-readable via RLS. Server functions can layer on top later
// for cached/edge public API endpoints.
import { supabase } from "@/integrations/supabase/client";

export interface CodexConcept {
  slug: string;
  title: string;
  essence: string | null;
  definition: string;
  cluster: string | null;
  sort_order: number;
}

export interface CodexRelation {
  from_slug: string;
  to_slug: string;
  kind: string;
  weight: number;
}

export interface CodexLink {
  concept_slug: string;
  entity_kind: string;
  entity_ref: string;
  label: string | null;
  weight: number;
}

export async function listConcepts(): Promise<CodexConcept[]> {
  const { data, error } = await supabase
    .from("codex_concepts")
    .select("slug, title, essence, definition, cluster, sort_order")
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return (data ?? []) as CodexConcept[];
}

export async function getConcept(slug: string): Promise<CodexConcept | null> {
  const { data, error } = await supabase
    .from("codex_concepts")
    .select("slug, title, essence, definition, cluster, sort_order")
    .eq("slug", slug)
    .maybeSingle();
  if (error) throw error;
  return (data as CodexConcept | null) ?? null;
}

export async function getConceptRelations(slug: string): Promise<CodexRelation[]> {
  const { data, error } = await supabase
    .from("codex_relations")
    .select("from_slug, to_slug, kind, weight")
    .or(`from_slug.eq.${slug},to_slug.eq.${slug}`);
  if (error) throw error;
  return (data ?? []) as CodexRelation[];
}

export async function getConceptLinks(slug: string): Promise<CodexLink[]> {
  const { data, error } = await supabase
    .from("codex_links")
    .select("concept_slug, entity_kind, entity_ref, label, weight")
    .eq("concept_slug", slug)
    .order("weight", { ascending: false });
  if (error) throw error;
  return (data ?? []) as CodexLink[];
}

/** Concepts linked to a given platform entity (e.g. gate 12, book slug). */
export async function conceptsForEntity(
  entity_kind: string,
  entity_ref: string,
): Promise<CodexConcept[]> {
  const { data, error } = await supabase
    .from("codex_links")
    .select("concept:codex_concepts(slug, title, essence, definition, cluster, sort_order)")
    .eq("entity_kind", entity_kind)
    .eq("entity_ref", entity_ref);
  if (error) throw error;
  type Row = { concept: CodexConcept | null };
  return ((data ?? []) as unknown as Row[])
    .map((r) => r.concept)
    .filter((c): c is CodexConcept => !!c);
}
